import { QueryClient, useQuery } from "@tanstack/react-query";
import apiClient from "services/ApiClient";
import { MeetingEvent } from "../models";
import { MeetingEventsFilters } from "./filters";

export * from "./filters";

export const meetingEventsKeys = {
  all: ["meetingEvents"] as const,
  lists: () => [...meetingEventsKeys.all, "list"] as const,
  list: (filters: MeetingEventsFilters) => [...meetingEventsKeys.lists(), filters] as const,
  details: () => [...meetingEventsKeys.all, "detail"] as const,
  detail: (id: string) => [...meetingEventsKeys.details(), id] as const,
};

const buildQueryString = (filters: MeetingEventsFilters): string => {
  const params = new URLSearchParams();

  Object.entries(filters).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== "") {
      params.append(key, String(value));
    }
  });

  return params.toString();
};

export function inserMeetingEventInCache(
  queryClient: QueryClient,
  newEvent: MeetingEvent,
  filters?: MeetingEventsFilters,
) {
  queryClient.setQueryData<MeetingEvent[]>(
    filters ? meetingEventsKeys.list(filters) : meetingEventsKeys.lists(),
    (old) => {
      if (!old || old.length === 0) {
        return [newEvent];
      }

      const exists = old.some((item) => item.id === newEvent.id);

      if (exists) {
        return old.map((item) => (item.id === newEvent.id ? newEvent : item));
      }
      return [...old, newEvent];
    },
  );
}

export const useMeetingEventsQuery = (
  filters: MeetingEventsFilters,
  options = {},
) => {
  const queryString = buildQueryString(filters);

  return useQuery<MeetingEvent[]>({
    queryKey: meetingEventsKeys.list(filters),
    queryFn: async () => {
      const url = queryString
        ? `/api/meeting-events?${queryString}`
        : "/api/meeting-events";
      return apiClient.get(url);
    },
    staleTime: 1000 * 60 * 3, // 3 minutes
    ...options,
  });
};

export const useMeetingEventDetailQuery = (id: string | undefined, options = {}) => useQuery<MeetingEvent | null>({
  queryKey: meetingEventsKeys.detail(id as string),
  queryFn: async () => apiClient.get(`/api/meeting-events/${id}`),
  staleTime: 1000 * 60 * 3, // 3 minutes
  enabled: !!id && ((options as { enabled?: boolean }).enabled ?? true),
  ...options,
});
